import React, { useEffect, useState } from 'react';
import api from '../../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { X as XIcon, Image as ImageIcon } from '@phosphor-icons/react';

const STATUS_COLOR = {
  allocated: '#FF8C00',
  accepted: '#FF8C00',
  in_progress: '#002FA7',
  pickup_reached: '#002FA7',
  pickup_done: '#002FA7',
  drop_reached: '#002FA7',
  completed: '#00C853',
  cancelled: '#999999',
  pending: '#999999',
};

const fmtDT = (iso) => {
  if (!iso) return '-';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '-';
    return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  } catch { return '-'; }
};

const PhotoCell = ({ url, label, onClick }) => {
  if (!url) return <span className="text-[#999]">-</span>;
  return (
    <button
      type="button"
      onClick={() => onClick(url, label)}
      className="text-[#002FA7] underline text-xs flex items-center gap-1 hover:text-[#001f6f]"
      title={`View ${label}`}
    >
      <ImageIcon size={12} weight="bold" /> {label}
    </button>
  );
};

/**
 * Phase 70 — Trip-list popup for the Pilot Scorecard. Opens when the user
 * clicks Trips/Completed/Allocated/InLive count cells. Pulls drilldown data
 * from /admin/scorecard/{pilot_id}/trips with the GPS-computed timings.
 *
 * Props:
 *   open        bool
 *   onClose()   fn
 *   pilot       { id, name } | null
 *   bucket      'all' | 'completed' | 'allocated' | 'inlive'
 *   fromDate    YYYY-MM-DD
 *   toDate      YYYY-MM-DD
 */
export default function ScorecardTripsDialog({ open, onClose, pilot, bucket, fromDate, toDate }) {
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(false);
  const [photo, setPhoto] = useState(null); // {url, label}
  const apiBase = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    if (!open || !pilot?.id) return;
    setLoading(true);
    api
      .get(`/admin/scorecard/${pilot.id}/trips`, { params: { bucket, from_date: fromDate, to_date: toDate } })
      .then(r => setTrips(r.data?.trips || []))
      .catch(() => setTrips([]))
      .finally(() => setLoading(false));
  }, [open, pilot?.id, bucket, fromDate, toDate]);

  const openPhoto = (url, label) => {
    const full = url?.startsWith('/') ? `${apiBase}${url}` : url;
    setPhoto({ url: full, label });
  };

  const bucketLabel = {
    all: 'All Trips',
    completed: 'Completed Trips',
    allocated: 'Allocated Trips',
    inlive: 'In-Live Trips',
  }[bucket] || 'Trips';

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
        <DialogContent className="max-w-[95vw] w-[95vw] max-h-[92vh] overflow-hidden flex flex-col p-0">
          <DialogHeader className="px-5 pt-4 pb-2 border-b border-[#E5E5E5]">
            <DialogTitle className="flex items-center gap-2 text-base">
              <span>{bucketLabel}</span>
              <span className="text-xs text-[#666] font-normal">— {pilot?.name}</span>
              <span className="ml-auto text-xs text-[#666] font-normal">{trips.length} trips · {fromDate} → {toDate}</span>
            </DialogTitle>
            <DialogDescription className="sr-only">Pilot trip drill-down with GPS timings and photos</DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-auto px-5 pb-5">
            {loading ? (
              <p className="text-center py-12 text-sm text-[#666]">Loading trips…</p>
            ) : trips.length === 0 ? (
              <p className="text-center py-12 text-sm text-[#666]">No {bucketLabel.toLowerCase()} for this period.</p>
            ) : (
              <Table className="text-xs" data-testid="scorecard-trips-table">
                <TableHeader>
                  <TableRow className="bg-[#F3F3F3]">
                    <TableHead className="text-[10px] uppercase tracking-widest">Trip ID</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-widest">Pickup</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-widest">Drop</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-widest">Item</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-widest">Pilot</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-widest text-center">Helpers</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-widest">Priority</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-widest">Status</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-widest">Booked At</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-widest">Booked By</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-widest">Allocated At</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-widest">Trip Started</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-widest">Drive→Pickup</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-widest">GPS Pickup Wait</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-widest">Drive→Drop</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-widest">GPS Drop Wait</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-widest">DC Photo</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-widest">Goods Photo</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {trips.map(t => (
                    <TableRow key={t.id} data-testid={`scorecard-trip-row-${t.id}`}>
                      <TableCell className="font-mono">{t.id}</TableCell>
                      <TableCell className="max-w-[150px] truncate" title={t.pickup_supplier_name}>{t.pickup_supplier_name}</TableCell>
                      <TableCell className="max-w-[150px] truncate" title={t.drop_supplier_name}>{t.drop_supplier_name}</TableCell>
                      <TableCell className="max-w-[120px] truncate" title={`${t.item || ''} ${t.qty || ''} ${t.uom || ''}`}>
                        {t.item}{t.qty ? ` · ${t.qty}` : ''}{t.uom ? ` ${t.uom}` : ''}
                      </TableCell>
                      <TableCell>{t.pilot_name || '-'}</TableCell>
                      <TableCell className="text-center">{t.helpers_count}</TableCell>
                      <TableCell>{t.priority || '-'}</TableCell>
                      <TableCell>
                        <span
                          className="px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-widest text-white"
                          style={{ background: STATUS_COLOR[t.status] || '#999' }}
                        >
                          {t.status?.replace(/_/g, ' ') || '-'}
                        </span>
                      </TableCell>
                      <TableCell>{fmtDT(t.created_at)}</TableCell>
                      <TableCell className="max-w-[120px]">
                        {t.booked_by_name ? (
                          <>
                            <p className="truncate">{t.booked_by_name}</p>
                            {t.booked_by_mobile && (
                              <a href={`tel:${t.booked_by_mobile}`} className="text-[10px] text-[#002FA7] underline">
                                {t.booked_by_mobile}
                              </a>
                            )}
                          </>
                        ) : '-'}
                      </TableCell>
                      <TableCell>{fmtDT(t.allocated_at)}</TableCell>
                      <TableCell>{fmtDT(t.started_at)}</TableCell>
                      <TableCell>
                        {t.gps_drive_to_pickup_minutes != null
                          ? <span className="font-medium">{t.gps_drive_to_pickup_minutes}m</span>
                          : <span className="text-[#999]">-</span>}
                        {t.google_kms ? <span className="text-[10px] text-[#666] block">{t.google_kms} km</span> : null}
                      </TableCell>
                      <TableCell>
                        {t.gps_pickup_waiting_minutes != null
                          ? <span className="font-medium" title={`Reached ${fmtDT(t.gps_pickup_reached_at)} → Left ${fmtDT(t.gps_pickup_left_at)}`}>{t.gps_pickup_waiting_minutes}m</span>
                          : <span className="text-[#999]">-</span>}
                        {t.pickup_waiting_minutes != null && (
                          <span className="text-[10px] text-[#999] block">manual: {t.pickup_waiting_minutes}m</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {t.gps_drive_to_drop_minutes != null
                          ? <span className="font-medium">{t.gps_drive_to_drop_minutes}m</span>
                          : <span className="text-[#999]">-</span>}
                      </TableCell>
                      <TableCell>
                        {t.gps_drop_waiting_minutes != null
                          ? <span className="font-medium" title={`Reached ${fmtDT(t.gps_drop_reached_at)} → Left ${fmtDT(t.gps_drop_left_at)}`}>{t.gps_drop_waiting_minutes}m</span>
                          : <span className="text-[#999]">-</span>}
                        {t.drop_waiting_minutes != null && (
                          <span className="text-[10px] text-[#999] block">manual: {t.drop_waiting_minutes}m</span>
                        )}
                      </TableCell>
                      <TableCell><PhotoCell url={t.dc_photo} label="DC" onClick={openPhoto} /></TableCell>
                      <TableCell><PhotoCell url={t.goods_photo} label="Goods" onClick={openPhoto} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Photo lightbox */}
      {photo && (
        <div
          className="fixed inset-0 z-[60] bg-black/85 flex items-center justify-center p-4"
          onClick={() => setPhoto(null)}
        >
          <button
            onClick={() => setPhoto(null)}
            className="absolute top-4 right-4 text-white/70 hover:text-white p-2 z-10"
            data-testid="scorecard-photo-close"
            aria-label="Close"
          >
            <XIcon size={24} weight="bold" />
          </button>
          <div className="max-w-[90vw] max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
            <p className="text-white/70 text-xs uppercase tracking-widest mb-2 text-center">{photo.label}</p>
            <img src={photo.url} alt={photo.label} className="max-w-[90vw] max-h-[80vh] object-contain border border-white/20" />
          </div>
        </div>
      )}
    </>
  );
}
